import modalStyle from "../../modals/modal.module.scss";

import icn_btn_rediscover from "assets/images/icons/icn_btn_rediscover.svg";
import img_device_camera from "assets/images/icons/img_device_camera.svg";
import img_device_nvr from "assets/images/icons/img_device_nvr.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import CustomMenu from "components/Menu";
import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { getDefaultTableProp } from "../../theme";
import { scopeLevelOption, surveillanceDeviceTypeOption } from "../const";
import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up_light.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down_light.svg";

const DeviceTable = ({ currentStep, devices, results, loading }) => {
  const showResult = currentStep === 1;

  const getResult = (device) => {
    const result = results.find((item) => item.deviceId === device.deviceId);

    if (!result) {
      if (loading) {
        return <CircularProgress size={20} />;
      }

      return (
        <img
          src={icn_btn_rediscover}
          alt="discovery"
          style={{ width: "20px", height: "20px", marginRight: "5px" }}
        />
      );
    }

    return (
      <span style={{ color: result.isAdopted ? "#00FF00" : "#FF0000", fontWeight: "bold" }}>
        {result.isAdopted ? "Success" : "Failed"}
      </span>
    );
  };

  return (
    <div className={modalStyle.modal_Block} style={{ margin: "20px 0", maxHeight: "400px" }}>
      <TableContainer style={{ overflowY: "auto" }}>
        <Table>
          <TableHead sx={{ ...getDefaultTableProp().tableHead.sx }}>
            <TableRow sx={{ ...getDefaultTableProp().tableHead.tableRow.sx }}>
              <TableCell sx={{ ...getDefaultTableProp().tableHead.tableCell.text }}>
                Model Name
              </TableCell>
              <TableCell sx={{ ...getDefaultTableProp().tableHead.tableCell.text }}>
                MAC Address
              </TableCell>
              <TableCell sx={{ ...getDefaultTableProp().tableHead.tableCell.text }}>
                IP Address
              </TableCell>
              {showResult && (
                <TableCell sx={{ ...getDefaultTableProp().tableHead.tableCell.text }}>
                  Result
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <tbody>
            {devices.map((device, rowIndex) => {
              const isNvr = device.deviceType?.value === surveillanceDeviceTypeOption.nvr.value;

              return (
                <TableRow
                  key={`${device.deviceId}-row`}
                  sx={{ ...getDefaultTableProp(rowIndex).tableBody.tableRow }}
                >
                  <TableCell sx={{ ...getDefaultTableProp().tableBody.tableCell.text }}>
                    <Box display="flex" alignItems="center">
                      <img
                        src={isNvr ? img_device_nvr : img_device_camera}
                        alt=""
                        style={{ width: "60px", marginRight: "8px" }}
                      />
                      {device.modelName}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...getDefaultTableProp().tableBody.tableCell.text }}>
                    {device.deviceId}
                  </TableCell>
                  <TableCell sx={{ ...getDefaultTableProp().tableBody.tableCell.text }}>
                    {device.ipAddress}
                  </TableCell>
                  {showResult && (
                    <TableCell sx={{ ...getDefaultTableProp().tableBody.tableCell.text }}>
                      {getResult(device)}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
    </div>
  );
};

const SiteSetting = ({
  scopeLevel,
  sites,
  selectedSiteId,
  setSelectedSiteId,
  setAdoptStatus,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const menuOpen = Boolean(anchorEl);

  const getSiteId = () => {
    const site = siteOptions.find((item) => item.selected);
    return site ? site.id : "none";
  };
  const selectedSite = siteOptions.find((item) => item.id === getSiteId());
  const selectableSites = siteOptions.filter((site) => site.id !== "inventory");
  const inventorySite = siteOptions.find((site) => site.id === "inventory");

  const handleClose = () => {
    if (document.activeElement) {
      document.activeElement.blur();
    }

    setAnchorEl(null);
  };

  const handleSelectSite = (siteId) => {
    setSelectedSiteId(siteId);
    handleClose();
  };

  useEffect(() => {
    setAdoptStatus(
      scopeLevel === scopeLevelOption.site || (selectedSiteId && selectedSiteId !== "none")
    );
  }, [scopeLevel, selectedSiteId, setAdoptStatus]);

  useEffect(() => {
    const activeSiteId = selectedSiteId || "none";
    const updatedSiteOptions = [
      {
        id: "none",
        label: "Select site",
        selected: activeSiteId === "none",
      },
      ...sites.map((site) => ({
        id: site.id,
        label: site.name,
        selected: activeSiteId === site.id,
      })),
      {
        id: "inventory",
        label: "Inventory",
        selected: activeSiteId === "inventory",
      },
    ];

    setSiteOptions(updatedSiteOptions);
  }, [selectedSiteId, sites]);

  return (
    <>
      {scopeLevel === scopeLevelOption.org && (
        <>
          <Box sx={{ padding: "10px 20px", textAlign: "center" }}>
            Please ensure the devices' IP settings allow them to reach the controller before proceeding. Incorrect IP settings may prevent the devices from being adopted successfully.
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            sx={{ margin: "20px 20px 30px 20px" }}
          >
            <Box className={modalStyle.field} style={{ marginRight: "30px" }}>
              Add to site
            </Box>
            <Box className={modalStyle.item}>
              <Box className={modalStyle.container}>
                <Box
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #707ca8",
                    borderRadius: "8px",
                    width: "300px",
                    height: 32,
                    bgcolor: "#fff",
                    position: "relative",
                    cursor: "pointer",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography
                    sx={{
                      px: "10px",
                      pr: "42px",
                      fontSize: theme.typography.customFontSize[2],
                      color: getSiteId() === "none" ? "primary.greyThread" : "primary.main",
                      fontWeight: theme.typography.customFontWeight[3],
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    }}
                  >
                    {selectedSite?.label || "Select site"}
                  </Typography>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 32,
                      height: "100%",
                      bgcolor: "#172264",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderTopRightRadius: 6,
                      borderBottomRightRadius: 6,
                    }}
                  >
                    {menuOpen ? <ChevronUp /> : <ChevronDown />}
                  </Box>
                </Box>
                <CustomMenu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  handleMenuClose={handleClose}
                  maxWidth={300}
                >
                  <List
                    sx={{
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      padding: "5px 10px",
                      maxHeight: "180px",
                      overflowY: "auto",
                      overflowX: "hidden",
                      boxSizing: "content-box",
                    }}
                  >
                    {selectableSites.map((site) => (
                      <ListItem
                        key={`${site.id}-site`}
                        button
                        selected={getSiteId() === site.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelectSite(site.id);
                        }}
                        sx={{
                          color: site.id === "none" ? "primary.greyThread" : "primary.main",
                          margin: "5px 5px 0 0",
                          borderRadius: "5px",
                          width: "272px",
                          height: "40px",
                          "&:hover": { backgroundColor: "#e8ebfc" },
                          "&.Mui-selected": { backgroundColor: "#dbe0fc" },
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{
                            style: {
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              fontSize: theme.typography.customFontSize[2],
                            },
                          }}
                          primary={site.label}
                        />
                      </ListItem>
                    ))}
                    <Divider sx={{ my: "5px", mx: "20px" }} />
                    {inventorySite && (
                      <ListItem
                        key="inventory-site"
                        button
                        selected={getSiteId() === inventorySite.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSelectSite(inventorySite.id);
                        }}
                        sx={{
                          color: "primary.main",
                          margin: "5px 5px 0 0",
                          borderRadius: "5px",
                          width: "272px",
                          height: "40px",
                          "&:hover": { backgroundColor: "#e8ebfc" },
                          "&.Mui-selected": { backgroundColor: "#dbe0fc" },
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{
                            style: {
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              fontSize: theme.typography.customFontSize[2],
                            },
                          }}
                          primary={inventorySite.label}
                        />
                      </ListItem>
                    )}
                  </List>
                </CustomMenu>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

const ModalAdoption = ({
  display,
  currentStep,
  scopeLevel,
  sites,
  devices,
  results,
  loading,
  canClose,
  selectedSiteId,
  setSelectedSiteId,
  onCancel,
  onAdopt,
  onClose,
}) => {
  const [adoptStatus, setAdoptStatus] = useState(false);

  return (
    <ModalContainer display={display} modalWidth="90p">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography color="#707ca8" fontWeight="600" fontSize="20px">
          Device Adoption
        </Typography>
        <Button
          disabled={currentStep === 1 && !canClose}
          onClick={() => {
            if (currentStep === 1 && !canClose) {
              return;
            }

            if (currentStep === 1) {
              onClose();
              return;
            }

            onCancel();
          }}
          className={`${currentStep === 1 && !canClose ? modalStyle.disabled_closeBtn : ""}`}
          sx={{
            marginRight: "-20px",
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <DeviceTable currentStep={currentStep} devices={devices} results={results} loading={loading} />
      {currentStep === 0 && (
        <SiteSetting
          scopeLevel={scopeLevel}
          sites={sites}
          selectedSiteId={selectedSiteId}
          setSelectedSiteId={setSelectedSiteId}
          setAdoptStatus={setAdoptStatus}
        />
      )}
      <Box className={modalStyle.modal_operation}>
        {currentStep === 0 ? (
          <>
            <OutlineButton
              className={modalStyle.modal_cancelBtn}
              style={{ width: "195px", height: "42px", fontSize: "16px" }}
              variant="contained"
              onClick={onCancel}
            >
              Cancel
            </OutlineButton>
            <SolidButton
              className={`${modalStyle.modal_saveBtn} ${!adoptStatus ? modalStyle.disabled_btn : ""}`}
              disabled={!adoptStatus}
              style={{ width: "195px", height: "42px", fontSize: "16px" }}
              variant="contained"
              onClick={onAdopt}
            >
              Next
            </SolidButton>
          </>
        ) : (
          <SolidButton
            className={`${modalStyle.modal_saveBtn} ${!canClose ? modalStyle.disabled_btn : ""}`}
            disabled={!canClose}
            style={{ width: "195px", height: "42px", fontSize: "16px" }}
            variant="contained"
            onClick={onClose}
          >
            Close
          </SolidButton>
        )}
      </Box>
    </ModalContainer>
  );
};

export default ModalAdoption;
